import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import {
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft,
  Shield, Truck, Tag, Package, CheckCircle,
  ChevronRight, RotateCcw, Zap
} from 'lucide-react';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const savings = Math.round(getCartTotal() * 0.2);

  /* ── Empty cart ── */
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200 max-w-sm w-full">
          <ShoppingCart className="w-20 h-20 text-gray-200 mx-auto mb-6" strokeWidth={1} />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Your cart is empty!</h2>
          <p className="text-sm text-gray-500 mb-6">Add items to it now.</p>
          <Link to="/"
            className="block w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors text-sm"
          >
            Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4">
      <div className="max-w-6xl mx-auto px-4">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
          <Link to="/" className="hover:text-orange-600">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-800 font-semibold">Shopping Cart</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 items-start">

          {/* ── LEFT: Cart Items ── */}
          <div className="flex-1 space-y-3">

            {/* Delivery banner */}
            <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-green-600 shrink-0" />
              <p className="text-sm text-gray-700">
                Your order is eligible for <span className="font-bold text-green-600">FREE Delivery</span>.
                Select this option at checkout.
              </p>
            </div>

            {/* Cart header */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900">
                  Shopping Cart
                  <span className="text-sm font-normal text-gray-500 ml-2">({cart.length} item{cart.length > 1 ? 's' : ''})</span>
                </h1>
                <span className="text-sm font-bold text-gray-700 hidden sm:block">Price</span>
              </div>

              {/* Items */}
              {cart.map((item, idx) => (
                <div key={item.cartItemId || item.id}
                  className={`px-5 py-5 flex flex-col sm:flex-row gap-4 ${idx < cart.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  {/* Image */}
                  <div className="w-full sm:w-32 h-32 bg-white rounded-lg border border-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                    {(item.variant?.image_url || item.image_url)
                      ? <img src={item.variant?.image_url || item.image_url} alt={item.name} className="w-full h-full object-contain p-2 product-img-clean" />
                      : <Package className="w-10 h-10 text-gray-200" />
                    }
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 line-clamp-2 mb-1">{item.name}</h3>
                        {item.variant_name && (
                          <p className="text-sm text-blue-600 font-medium mb-1">
                            Variant: {item.variant_name}
                          </p>
                        )}
                        {item.sku && (
                          <p className="text-xs text-gray-400 mb-1">SKU: {item.sku}</p>
                        )}
                        <p className="text-xs text-gray-500 mb-1">{item.category}</p>
                        <div className="flex items-center gap-1 mb-2">
                          <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                          <span className="text-xs text-green-600 font-semibold">In Stock</span>
                        </div>
                        <div className="flex items-center gap-1 mb-3">
                          <Tag className="w-3 h-3 text-orange-500" />
                          <span className="text-xs text-orange-600 font-semibold">
                            You save ₹{Math.round(item.price * 0.2).toLocaleString()} (20% off)
                          </span>
                        </div>

                        {/* Qty + actions */}
                        <div className="flex flex-wrap items-center gap-3">
                          {/* Quantity selector */}
                          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                            <button
                              onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors border-r border-gray-300"
                            >
                              <Minus className="w-3 h-3" strokeWidth={2.5} />
                            </button>
                            <span className="w-10 text-center text-sm font-bold text-gray-900">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors border-l border-gray-300"
                            >
                              <Plus className="w-3 h-3" strokeWidth={2.5} />
                            </button>
                          </div>

                          <span className="text-gray-300">|</span>

                          <button
                            onClick={() => removeFromCart(item.cartItemId || item.id)}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-red-600 hover:underline transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>

                          <span className="text-gray-300">|</span>

                          <button className="flex items-center gap-1 text-sm text-blue-600 hover:underline transition-colors">
                            <RotateCcw className="w-3.5 h-3.5" /> Save for later
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-gray-900">₹{(item.price * item.quantity).toLocaleString()}</p>
                        <p className="text-xs text-gray-400 line-through">₹{Math.round(item.price * item.quantity * 1.2).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Subtotal bottom */}
              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 text-right">
                <p className="text-base text-gray-700">
                  Subtotal ({cart.reduce((a, i) => a + i.quantity, 0)} item{cart.reduce((a, i) => a + i.quantity, 0) > 1 ? 's' : ''}):
                  <span className="font-bold text-gray-900 ml-2">₹{getCartTotal().toLocaleString()}</span>
                </p>
              </div>
            </div>

            {/* EMI / offers strip */}
            <div className="bg-white rounded-lg border border-gray-200 px-5 py-4 flex flex-wrap gap-4">
              {[
                { icon: Zap, text: '0% EMI available on select cards', color: 'text-blue-600' },
                { icon: Shield, text: '100% secure payments', color: 'text-green-600' },
                { icon: RotateCcw, text: 'Easy 30-day returns', color: 'text-orange-500' },
              ].map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <o.icon className={`w-4 h-4 ${o.color} shrink-0`} />
                  <span className="text-xs text-gray-600">{o.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Order Summary ── */}
          <div className="w-full lg:w-80 shrink-0 space-y-3 lg:sticky lg:top-4">

            {/* Savings box */}
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-green-600 shrink-0" />
              <p className="text-sm text-green-700 font-semibold">
                You're saving <span className="font-bold">₹{savings.toLocaleString()}</span> on this order!
              </p>
            </div>

            {/* Summary card */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Order Summary</h2>
              </div>

              <div className="px-5 py-4 space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Price ({cart.reduce((a, i) => a + i.quantity, 0)} items)</span>
                  <span>₹{Math.round(getCartTotal() * 1.2).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-green-600 font-semibold">
                  <span>Discount</span>
                  <span>− ₹{savings.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Delivery Charges</span>
                  <span className="text-green-600 font-semibold">FREE</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Secured Packaging</span>
                  <span className="text-green-600 font-semibold">FREE</span>
                </div>

                <div className="border-t border-dashed border-gray-200 pt-3 flex justify-between">
                  <span className="text-base font-bold text-gray-900">Total Amount</span>
                  <span className="text-base font-bold text-gray-900">₹{getCartTotal().toLocaleString()}</span>
                </div>

                <p className="text-xs text-green-600 font-semibold">
                  You will save ₹{savings.toLocaleString()} on this order
                </p>
              </div>

              {/* Checkout button */}
              <div className="px-5 pb-5">
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors text-sm flex items-center justify-center gap-2 shadow-md"
                >
                  Proceed to Checkout <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Trust badges */}
            <div className="bg-white rounded-lg border border-gray-200 px-5 py-4 space-y-3">
              {[
                { icon: Shield, label: 'Safe & Secure Payments', sub: '100% Authentic Products' },
                { icon: Truck,  label: 'Free Delivery',          sub: 'On all orders'           },
                { icon: RotateCcw, label: 'Easy Returns',        sub: '30-day return policy'    },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <b.icon className="w-5 h-5 text-orange-500 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-gray-800">{b.label}</p>
                    <p className="text-[10px] text-gray-400">{b.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Continue shopping */}
            <button
              onClick={() => navigate('/')}
              className="w-full py-2.5 bg-white border border-gray-300 hover:border-orange-400 text-gray-700 hover:text-orange-600 font-semibold rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> Continue Shopping
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CartPage;
